import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,TextField,
  TablePagination,
} from "@mui/material";
import { styled } from "@mui/system";
import Swal from "sweetalert2";
import "./leave.css";
import { GlobalStyles } from "@mui/system";
import { useAuth } from "../../../AuthProvider";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Container = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(9),
  marginLeft: theme.spacing(3),
  marginRight: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.common.white,
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
  fontSize: "1.5rem",
  textAlign: "center",
  fontWeight: "bold",
}));

const TableHeaderCell = styled(TableCell)(({ theme }) => ({
  color: "white",

  textAlign: "center",
  fontWeight: "bold",
  border: "0.1rem solid black",
}));

const TableCellStyled = styled(TableCell)(({ theme }) => ({
  color: "black",
  fontSize: "15px",
  textAlign: "center",
  borderRight: "1px solid #ddd",
}));

const ReasonTableCell = styled(TableCellStyled)({
  width: "25%",
  textAlign: "center",
});

const DefaultTableCell = styled(TableCellStyled)({
  width: "10%",
  textAlign: "center",

});

const ButtonCell = styled(TableCellStyled)({
  width: "8%",
  textAlign: "center",
});
const baseUrl = process.env.REACT_APP_API_BASE_URL;
const EmployeeLeave = () => {
  const [attendanceList, setAttendanceList] = useState([]);
  
  const [loader, setLoader] = useState(true);
  const { oId ,pId} = useAuth();

  // State for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);


  const [editRowId, setEditRowId] = useState(null); // currently editing row
const [editedRows, setEditedRows] = useState({}); // track edited values per row
const [status, setStatus] = useState({}); // track status changes per row
const [loadingSave, setLoadingSave] = useState({});
const [disabledSaveButtons, setDisabledSaveButtons] = useState({});



// Helper to capitalize first letter
// const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
 
  const fetchLeaveRequestList = async () => {
  try {
    setLoader(true);
    const response = await fetch(`${baseUrl}/api/leave/allLeaveRequests/${oId}/${pId}`);
    const data = await response.json();

    // Normalize status to lowercase for state/storage
    const normalizedData = data.map(item => ({
      ...item,
      status: (item.status || "pending").toLowerCase()
    }));

    // Step 1: Reverse
    const reversedData = normalizedData.reverse();

    // Step 2: Sort
    const statusOrder = { pending: 0, approved: 1, rejected: 2 };
    const sortedData = reversedData.sort((a, b) =>
      statusOrder[a.status] - statusOrder[b.status]
    );

    setAttendanceList(sortedData);

    const statusInit = sortedData.reduce((acc, item) => {
      acc[item.lrId] = item.status;
      return acc;
    }, {});
    setStatus(statusInit);

    const savedDisabledButtons = JSON.parse(sessionStorage.getItem("disabledSaveButtons")) || {};
    setDisabledSaveButtons(savedDisabledButtons);
  } catch (error) {
    console.error("Error fetching attendance list:", error);
  } finally {
    setLoader(false);
  }
};


    useEffect(() => {
    fetchLeaveRequestList();
  }, []);


const calculateDays = (startStr, endStr) => {
  const start = new Date(startStr);
  const end = new Date(endStr);

  if (end < start) return -1; // Invalid
  const diff = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
  return diff;
};





const handleEdit = (lrId, rowData) => {
  setEditRowId(lrId);
  setEditedRows((prev) => ({
    ...prev,
    [lrId]: {
      ...rowData,
      startDate: rowData.startDate,
      endDate: rowData.endDate,
      reason: rowData.reason,
      noOfDays: rowData.noOfDays,
    },
  }));
  setStatus((prev) => ({
    ...prev,
    [lrId]: rowData.status,
  }));
};


const handleInputChange = (lrId, field, value) => {
  setEditedRows((prev) => {
    const currentRow = prev[lrId];
    const updated = { ...currentRow, [field]: value };

    if (field === "endDate") {
      const days = calculateDays(currentRow.startDate, value);
      updated.noOfDays = days >= 0 ? days : 0; // Prevent negative days
    }

    return { ...prev, [lrId]: updated };
  });
};





const handleCloseEdit = (lrId) => {
  setEditRowId(null);
  setEditedRows((prev) => {
    const updated = { ...prev };
    delete updated[lrId];
    return updated;
  });

  setStatus((prev) => {
    const updated = { ...prev };
    delete updated[lrId]; // optional: revert status change
    return updated;
  });
};



const handleStatusChange = (lrId, newStatus) => {
  setStatus((prevStatus) => ({
    ...prevStatus,
    [lrId]: newStatus, // keep it in lowercase
  }));
};



const handleSave = async (lrId, originalRow) => {
  const edited = editedRows[lrId] || {};

  const startDate = originalRow.startDate;
  const endDate = edited.endDate || originalRow.endDate;
  const reason = edited.reason ?? originalRow.reason;
  const noOfDays = edited.noOfDays ?? calculateDays(startDate, endDate);
  const selectedStatus = status[lrId] ?? originalRow.status;

  if (new Date(endDate) < new Date(startDate)) {
    toast.error("End Date cannot be earlier than Start Date");
    return;
  }

  const hasChanged =
    originalRow.endDate !== endDate ||
    originalRow.reason !== reason ||
    originalRow.status !== selectedStatus;

  if (!hasChanged) {
    toast.info("No changes made");
    return;
  }

  try {
    setLoadingSave((prev) => ({ ...prev, [lrId]: true }));

    const queryParams = new URLSearchParams({
      startDate,
      endDate,
      noOfDays: noOfDays.toString(),
      reason,
      status: selectedStatus, // ✅ Add status if backend supports
    });

    const url = `${baseUrl}/api/leave/admin/update/custom/employeeId/${lrId}?${queryParams.toString()}`;

    const response = await fetch(url, {
      method: "PATCH",
    });

    const responseText = await response.text();
    if (!response.ok) throw new Error(responseText);

    // toast.success(responseText || "Updated successfully");
toast.success(responseText || "Updated successfully", {
            position: "top-right",
            autoClose: 1000,
          });
    setEditRowId(null);
    setEditedRows((prev) => {
      const copy = { ...prev };
      delete copy[lrId];
      return copy;
    });
    fetchLeaveRequestList();
  } catch (err) {
    toast.error(err.message || "Error saving changes", {position: "top-right",
            autoClose: 2000,});
  } finally {
    setLoadingSave((prev) => ({ ...prev, [lrId]: false }));
  }
};






 
 const getStatusColor = (status) => {
  switch (status) {
    case "pending":
      return "orange";
    case "approved":
      return "green";
    case "rejected":
      return "red";
    default:
      return "black";
  }
};

 
 
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container elevation={2} sx={{ marginTop: "84px" }}>
      <Title variant="h5" sx={{ marginBottom: "55px" }}>
        Employees Leave Management
      </Title>

      <GlobalStyles
        styles={{
          ".custom-sidebar-scrollbar": {
            scrollbarWidth: "thin", // For Firefox
            scrollbarColor: "grey #e0e0e0",
          },
          ".custom-sidebar-scrollbar::-webkit-scrollbar": {
            width: "6px", // For WebKit-based browsers
          },
          ".custom-sidebar-scrollbar::-webkit-scrollbar-track": {
            background: "#f1f1f1", // Track color
          },
          ".custom-sidebar-scrollbar::-webkit-scrollbar-thumb": {
            backgroundColor: "grey", // Thumb color
            borderRadius: "10px",
          },
          ".custom-sidebar-scrollbar::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#555",
          },
        }}
      />

      <TableContainer
        component={Paper}
        style={{ maxHeight: "700px", overflowY: "auto" }}
        className="custom-sidebar-scrollbar"
      >
        <Table stickyHeader>
          <TableHead className="tableHeader">
            <TableRow>
              <TableHeaderCell
                style={{
                  backgroundColor: "#fff9c4",
                  border: "1px solid #ddd",
                  fontSize: "14px",
                  padding: "4px",
                  color: "black",
                  width:'5px'
                }}
              >
                S.No
              </TableHeaderCell>
              <TableHeaderCell
                style={{
                  backgroundColor: "#fff9c4",
                  border: "1px solid #ddd",
                  fontSize: "14px",
                  padding: "4px",
                  color: "black",
                }}
              >
                Employe Name
              </TableHeaderCell>
               <TableHeaderCell
                style={{
                  backgroundColor: "#fff9c4",
                  border: "1px solid #ddd",
                  fontSize: "14px",
                  padding: "4px",
                  color: "black",
                }}
              >
                Leave Type
              </TableHeaderCell>
              <TableHeaderCell
                style={{
                  backgroundColor: "#fff9c4",
                  border: "1px solid #ddd",
                  fontSize: "14px",
                  padding: "4px",
                  color: "black",
                }}
              >
                Start Date
              </TableHeaderCell>
              <TableHeaderCell
                style={{
                  backgroundColor: "#fff9c4",
                  border: "1px solid #ddd",
                  fontSize: "14px",
                  padding: "4px",
                  color: "black",
                }}
              >
                End Date
              </TableHeaderCell>
              <TableHeaderCell
                style={{
                  backgroundColor: "#fff9c4",
                  border: "1px solid #ddd",
                  fontSize: "14px",
                  padding: "4px",
                  color: "black",
                }}
              >
                No Of Days
              </TableHeaderCell>
             
              <TableHeaderCell
                style={{
                  backgroundColor: "#fff9c4",
                  border: "1px solid #ddd",
                  fontSize: "14px",
                  padding: "4px",
                  color: "black",
                }}
              >
                Reason
              </TableHeaderCell>
              <TableHeaderCell
                style={{
                  backgroundColor: "#fff9c4",
                  border: "1px solid #ddd",
                  fontSize: "14px",
                  padding: "4px",
                  color: "black",
                }}
              >
                Status
              </TableHeaderCell>
              <TableHeaderCell
                style={{
                  backgroundColor: "#fff9c4",
                  border: "1px solid #ddd",
                  fontSize: "14px",
                  padding: "4px",
                  color: "black",
                }}
              >
                Action
              </TableHeaderCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loader ? (
              <TableRow>
                <TableCell colSpan={9} style={{ textAlign: "center", padding: "20px" }}>
                                                                                                  <Box display="flex" flexDirection="column" alignItems="center">
                                                                                                  <CircularProgress />
                                                                                                  <Typography variant="body2" sx={{ mt: 1 }}>
                                                                                                    Loading Data...
                                                                                                  </Typography>
                                                                                                </Box>
                                                                                              </TableCell>
              </TableRow>
            ) : attendanceList.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  style={{ textAlign: "center", padding: "20px" }}
                >
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              attendanceList
                // .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((employee, index) => (
                  <TableRow key={employee.lrId}>
                    <TableCell
                      style={{
                        borderRight: "1px solid #ddd",
                        padding: "2px", // Reduced padding
                        textAlign: "center",
                        maxWidth: "30px", // Reduce width
                        minWidth: "10px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {index + 1}
                    </TableCell>
                     <TableCell
  sx={{
    borderRight: "1px solid #ddd",
    padding: "2px",
    textAlign: "center",
    maxWidth: "80px",
    minWidth: "80px",
  }}
>
  <Box
    sx={{
      whiteSpace: "normal",       // Allow wrapping
      wordBreak: "break-word",    // Break long words
      overflowWrap: "break-word",
      fontSize: "13px",
    }}
  >
    {employee.firstName} {employee.lastName}
  </Box>
</TableCell>
                    <DefaultTableCell
                      style={{
                        color: "black",
                        fontSize: "13px",
                        textAlign: "center",
                        borderRight: "1px solid #ddd",
                      }}
                    >
                      {employee.leaveType}
                    </DefaultTableCell>
                    <TableCell
                      style={{
                        borderRight: "1px solid #ddd",
                        textAlign: "center",
                        fontSize: "13px",
                        padding: "1px",
                      
                      }}
                    >
                      {employee.startDate}
                    </TableCell>


<TableCell style={{
                        borderRight: "1px solid #ddd",
                        textAlign: "center",
                        fontSize: "13px",
                        padding: "1px",
                      
                      }}>
  {editRowId === employee.lrId ? (
    <TextField
      type="date"
      size="small"
      value={editedRows[employee.lrId]?.endDate || ""}
      onChange={(e) =>
        handleInputChange(employee.lrId, "endDate", e.target.value)
      }
      inputProps={{ min: employee.startDate }}
    />
  ) : (
    employee.endDate
  )}
</TableCell>


<TableCell style={{
                        borderRight: "1px solid #ddd",
                        textAlign: "center",
                        fontSize: "13px",
                        padding: "1px",
                      
                      }}>
  {editRowId === employee.lrId
    ? editedRows[employee.lrId]?.noOfDays || 0
    : employee.noOfDays}
</TableCell>



<TableCell
  sx={{
    border: "1px solid #ddd",
    textAlign: "center",
    fontSize: "13px",
    padding: "4px",
    maxWidth: "200px", // Prevents cell from expanding
  }}
>
  {editRowId === employee.lrId ? (
    <TextField
      size="small"
      multiline
      rows={2}
      value={editedRows[employee.lrId]?.reason || ""}
      onChange={(e) => handleInputChange(employee.lrId, "reason", e.target.value)}
      fullWidth
    />
  ) : (
    <Box
      sx={{
        maxHeight: "50px",
        overflowY: "auto",
        padding: "2px",
        textAlign: "center",
        whiteSpace: "pre-wrap", // Ensures text wraps correctly
        wordBreak: "break-word",
      }}
    >
      {employee.reason || "N/A"}
    </Box>
  )}
</TableCell>




<TableCell style={{
  borderRight: "1px solid #ddd",
  textAlign: "center",
  fontSize: "13px",
  padding: "1px",
}}>
 

  <select
  value={status[employee.lrId] || "pending"}
  onChange={(e) => handleStatusChange(employee.lrId, e.target.value)}
  style={{
    width: "90%",
    height: "35px",
    backgroundColor: editRowId === employee.lrId ? "#fff" : "#f9f9f9",
    color: getStatusColor(status[employee.lrId] || "pending"),
  }}
>
  <option value="" disabled>Select status</option>
  <option value="pending">Pending</option>
  <option value="approved">Approved</option>
  <option value="rejected">Rejected</option>
</select>

</TableCell>


<TableCell style={{
  borderRight: "1px solid #ddd",
  textAlign: "center",
  fontSize: "13px",
  padding: "1px",
}}>
  <Button
    variant="outlined"
    onClick={() => handleEdit(employee.lrId, employee)}
    disabled={editRowId === employee.lrId} // Disable edit only if already in edit mode
  >
    Edit
  </Button>
  <Button
    variant="contained"
    onClick={() => handleSave(employee.lrId, employee)}
  sx={{
  backgroundColor: "#3C3972",
  color: "#fff",
  marginLeft: 1,
  "&:hover": {
    backgroundColor: "#3C3972", // Same as normal
  },
}}

    // disabled={editRowId !== employee.lrId} // Save enabled only in edit mode
  >
    {loadingSave[employee.lrId] ? (
      <CircularProgress size={24} sx={{ color: "#fff" }} />
    ) : (
      "Save"
    )}
  </Button>
</TableCell> 


                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
       
      </TableContainer>
      <ToastContainer />
    </Container>
  );
};

export default EmployeeLeave;